import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Save, History, User, AlertTriangle, Check, Droplets } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tooth positions
const UPPER_TEETH = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16];
const LOWER_TEETH = [32, 31, 30, 29, 28, 27, 26, 25, 24, 23, 22, 21, 20, 19, 18, 17];

// Sites: MB=Mesio-Buccal, B=Buccal, DB=Disto-Buccal, ML=Mesio-Lingual, L=Lingual, DL=Disto-Lingual
const SITES = ['MB', 'B', 'DB', 'ML', 'L', 'DL'];
const BUCCAL_SITES = ['MB', 'B', 'DB'];
const LINGUAL_SITES = ['ML', 'L', 'DL'];

// Color coding for pocket depths
const getDepthColor = (depth) => {
  if (depth <= 3) return 'bg-green-100 text-green-800 border-green-300';
  if (depth <= 5) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  if (depth <= 6) return 'bg-orange-100 text-orange-800 border-orange-300';
  return 'bg-red-100 text-red-800 border-red-300';
};

const getDepthBgColor = (depth) => {
  if (depth <= 3) return '#22c55e';
  if (depth <= 5) return '#eab308';
  if (depth <= 6) return '#f97316';
  return '#ef4444';
};

// Periodontal tooth component
const PerioTooth = ({ number, data, onUpdate, isUpper }) => {
  const [localData, setLocalData] = useState(data || {
    probing_depths: [0, 0, 0, 0, 0, 0],
    bleeding_on_probing: [false, false, false, false, false, false],
    recession: [0, 0, 0, 0, 0, 0],
    mobility: 0,
    furcation: 0
  });

  useEffect(() => {
    if (data) setLocalData(data);
  }, [data]);

  const updateDepth = (siteIndex, value) => {
    const newDepths = [...localData.probing_depths];
    newDepths[siteIndex] = parseInt(value) || 0;
    const updated = { ...localData, probing_depths: newDepths };
    setLocalData(updated);
    onUpdate(number, updated);
  };

  const toggleBleeding = (siteIndex) => {
    const newBleeding = [...localData.bleeding_on_probing];
    newBleeding[siteIndex] = !newBleeding[siteIndex];
    const updated = { ...localData, bleeding_on_probing: newBleeding };
    setLocalData(updated);
    onUpdate(number, updated);
  };

  const updateRecession = (siteIndex, value) => {
    const newRecession = [...(localData.recession || [0, 0, 0, 0, 0, 0])];
    newRecession[siteIndex] = parseInt(value) || 0;
    const updated = { ...localData, recession: newRecession };
    setLocalData(updated);
    onUpdate(number, updated);
  };

  // Visual pocket depth bar
  const DepthBar = ({ depth, index }) => {
    const height = Math.min(depth * 8, 80);
    return (
      <div className="relative h-20 w-4 bg-slate-100 rounded-sm overflow-hidden">
        <div 
          className="absolute bottom-0 w-full transition-all duration-200"
          style={{ 
            height: `${height}%`, 
            backgroundColor: getDepthBgColor(depth) 
          }}
        />
        {localData.bleeding_on_probing[index] && (
          <div className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </div>
    );
  };

  return (
    <div className={`flex flex-col items-center ${isUpper ? '' : 'flex-col-reverse'}`}>
      {/* Buccal/Facial measurements (top for upper, bottom for lower) */}
      <div className={`flex gap-0.5 ${isUpper ? 'mb-1' : 'mt-1'}`}>
        {BUCCAL_SITES.map((site, i) => (
          <div key={site} className="flex flex-col items-center">
            <div className="flex gap-0.5 mb-0.5">
              <DepthBar depth={localData.probing_depths[i]} index={i} />
            </div>
            <input
              type="number"
              min="0"
              max="15"
              value={localData.probing_depths[i] || ''}
              onChange={(e) => updateDepth(i, e.target.value)}
              className={`w-6 h-5 text-[10px] text-center border rounded ${getDepthColor(localData.probing_depths[i])}`}
            />
            <button
              onClick={() => toggleBleeding(i)}
              className={`w-6 h-4 text-[8px] mt-0.5 rounded ${localData.bleeding_on_probing[i] ? 'bg-red-500 text-white' : 'bg-slate-100'}`}
            >
              {localData.bleeding_on_probing[i] ? 'BOP' : '-'}
            </button>
          </div>
        ))}
      </div>

      {/* Tooth number */}
      <div className="w-14 h-8 flex items-center justify-center bg-slate-200 rounded text-sm font-bold">
        {number}
      </div>

      {/* Lingual measurements */}
      <div className={`flex gap-0.5 ${isUpper ? 'mt-1' : 'mb-1'}`}>
        {LINGUAL_SITES.map((site, i) => {
          const siteIndex = i + 3;
          return (
            <div key={site} className="flex flex-col items-center">
              <button
                onClick={() => toggleBleeding(siteIndex)}
                className={`w-6 h-4 text-[8px] mb-0.5 rounded ${localData.bleeding_on_probing[siteIndex] ? 'bg-red-500 text-white' : 'bg-slate-100'}`}
              >
                {localData.bleeding_on_probing[siteIndex] ? 'BOP' : '-'}
              </button>
              <input
                type="number"
                min="0"
                max="15"
                value={localData.probing_depths[siteIndex] || ''}
                onChange={(e) => updateDepth(siteIndex, e.target.value)}
                className={`w-6 h-5 text-[10px] text-center border rounded ${getDepthColor(localData.probing_depths[siteIndex])}`}
              />
              <div className="flex gap-0.5 mt-0.5">
                <DepthBar depth={localData.probing_depths[siteIndex]} index={siteIndex} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PerioCharting = () => {
  const { token, user } = useAuth();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chartDate, setChartDate] = useState(new Date().toISOString().split('T')[0]);
  const [showHistory, setShowHistory] = useState(false);
  const [chartHistory, setChartHistory] = useState([]);
  
  // Chart data for all teeth
  const [chartData, setChartData] = useState({});

  useEffect(() => {
    fetchPatients();
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      fetchPerioData();
    }
  }, [selectedPatient, chartDate]);

  const fetchPatients = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/patients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPatients(response.data);
    } catch (error) {
      toast.error('Failed to load patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchPerioData = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dental/perio/${selectedPatient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert array to chart data object keyed by tooth number
      const dataByTooth = {};
      const todayEntries = response.data.filter(e => e.chart_date === chartDate);
      todayEntries.forEach(entry => {
        dataByTooth[entry.tooth_number] = {
          probing_depths: entry.probing_depths,
          bleeding_on_probing: entry.bleeding_on_probing,
          recession: entry.recession || [0,0,0,0,0,0],
          mobility: entry.mobility || 0,
          furcation: entry.furcation || 0
        };
      });
      setChartData(dataByTooth);
      setChartHistory(response.data);
    } catch (error) {
      setChartData({});
      setChartHistory([]);
    }
  };

  const handleToothUpdate = (toothNumber, data) => {
    setChartData(prev => ({
      ...prev,
      [toothNumber]: data
    }));
  };

  const saveChart = async () => {
    const teethWithData = Object.entries(chartData).filter(([_, data]) => 
      data.probing_depths.some(d => d > 0)
    );

    if (teethWithData.length === 0) {
      toast.error('Please enter measurements for at least one tooth');
      return;
    }

    setSaving(true);
    try {
      // Save each tooth's data
      for (const [toothNum, data] of teethWithData) {
        await axios.post(`${API_URL}/api/dental/perio`, {
          patient_id: selectedPatient.id,
          chart_date: chartDate,
          tooth_number: parseInt(toothNum),
          probing_depths: data.probing_depths,
          bleeding_on_probing: data.bleeding_on_probing,
          recession: data.recession,
          mobility: data.mobility,
          furcation: data.furcation,
          provider_id: user.id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(`Saved perio data for ${teethWithData.length} teeth`);
      fetchPerioData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save perio chart');
    } finally {
      setSaving(false);
    }
  };

  // Calculate summary stats
  const calculateStats = () => {
    const allDepths = Object.values(chartData).flatMap(d => d.probing_depths).filter(d => d > 0);
    const allBleeding = Object.values(chartData).flatMap(d => d.bleeding_on_probing);
    
    if (allDepths.length === 0) return { avg: 0, max: 0, pockets4plus: 0, pockets6plus: 0, bopPercent: 0 };
    
    const avg = (allDepths.reduce((a, b) => a + b, 0) / allDepths.length).toFixed(1);
    const max = Math.max(...allDepths);
    const pockets4plus = allDepths.filter(d => d >= 4).length;
    const pockets6plus = allDepths.filter(d => d >= 6).length;
    const bopCount = allBleeding.filter(Boolean).length;
    const bopPercent = allDepths.length > 0 ? Math.round((bopCount / allBleeding.length) * 100) : 0;
    
    return { avg, max, pockets4plus, pockets6plus, bopPercent };
  };

  const stats = calculateStats();

  const filteredPatients = patients.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6" data-testid="perio-charting">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Periodontal Charting</h1>
            <p className="text-slate-500">6-point probing depth measurements</p>
          </div>
          {selectedPatient && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowHistory(true)}>
                <History className="w-4 h-4 mr-2" />
                History
              </Button>
              <Button onClick={saveChart} disabled={saving} className="bg-teal-600 hover:bg-teal-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Chart'}
              </Button>
            </div>
          )}
        </div>

        {/* Patient Selection */}
        {!selectedPatient ? (
          <div className="card p-6">
            <h2 className="text-lg font-semibold mb-4">Select Patient</h2>
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search patients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {filteredPatients.map(patient => (
                <div
                  key={patient.id}
                  className="p-3 border rounded-lg cursor-pointer hover:bg-teal-50 hover:border-teal-300 transition-colors"
                  onClick={() => setSelectedPatient(patient)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium">
                      {patient.first_name?.charAt(0)}{patient.last_name?.charAt(0)}
                    </div>
                    <div>
                      <div className="font-medium">{patient.last_name}, {patient.first_name}</div>
                      <div className="text-sm text-slate-500">{patient.phone}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Patient Header */}
            <div className="card p-4 mb-4 flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center text-teal-700 font-medium text-lg">
                  {selectedPatient.first_name?.charAt(0)}{selectedPatient.last_name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-lg font-semibold">{selectedPatient.last_name}, {selectedPatient.first_name}</h2>
                  <p className="text-sm text-slate-500">DOB: {selectedPatient.date_of_birth}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <Label className="text-xs">Chart Date</Label>
                  <Input
                    type="date"
                    value={chartDate}
                    onChange={(e) => setChartDate(e.target.value)}
                    className="w-40"
                  />
                </div>
                <Button variant="outline" onClick={() => { setSelectedPatient(null); setChartData({}); }}>
                  Change Patient
                </Button>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-5 gap-4 mb-4">
              <div className="card p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{stats.avg}</div>
                <div className="text-xs text-slate-500">Avg Depth (mm)</div>
              </div>
              <div className="card p-3 text-center">
                <div className="text-2xl font-bold text-slate-800">{stats.max}</div>
                <div className="text-xs text-slate-500">Max Depth (mm)</div>
              </div>
              <div className="card p-3 text-center">
                <div className={`text-2xl font-bold ${stats.pockets4plus > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {stats.pockets4plus}
                </div>
                <div className="text-xs text-slate-500">Pockets ≥4mm</div>
              </div>
              <div className="card p-3 text-center">
                <div className={`text-2xl font-bold ${stats.pockets6plus > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {stats.pockets6plus}
                </div>
                <div className="text-xs text-slate-500">Pockets ≥6mm</div>
              </div>
              <div className="card p-3 text-center">
                <div className={`text-2xl font-bold ${stats.bopPercent > 30 ? 'text-red-600' : stats.bopPercent > 10 ? 'text-yellow-600' : 'text-green-600'}`}>
                  {stats.bopPercent}%
                </div>
                <div className="text-xs text-slate-500">BOP Sites</div>
              </div>
            </div>

            {/* Legend */}
            <div className="card p-3 mb-4">
              <div className="flex items-center justify-between">
                <div className="flex gap-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-green-500"></div>
                    <span>1-3mm (Healthy)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-yellow-500"></div>
                    <span>4-5mm (Moderate)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-orange-500"></div>
                    <span>6mm (Concerning)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-4 h-4 rounded bg-red-500"></div>
                    <span>7+mm (Severe)</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Droplets className="w-4 h-4 text-red-500" />
                    <span>Bleeding on Probing</span>
                  </div>
                </div>
                <div className="text-xs text-slate-500">
                  Sites: MB=Mesio-Buccal, B=Buccal, DB=Disto-Buccal, ML=Mesio-Lingual, L=Lingual, DL=Disto-Lingual
                </div>
              </div>
            </div>

            {/* Perio Chart */}
            <div className="card p-4 overflow-x-auto">
              {/* Upper Arch */}
              <div className="mb-2">
                <div className="text-xs text-slate-500 text-center mb-1">Maxillary (Upper) - Buccal</div>
                <div className="flex justify-center gap-1">
                  {UPPER_TEETH.map(num => (
                    <PerioTooth
                      key={num}
                      number={num}
                      data={chartData[num]}
                      onUpdate={handleToothUpdate}
                      isUpper={true}
                    />
                  ))}
                </div>
                <div className="text-xs text-slate-500 text-center mt-1">Lingual</div>
              </div>

              <div className="border-t-2 border-dashed border-slate-300 my-4" />

              {/* Lower Arch */}
              <div>
                <div className="text-xs text-slate-500 text-center mb-1">Lingual</div>
                <div className="flex justify-center gap-1">
                  {LOWER_TEETH.map(num => (
                    <PerioTooth
                      key={num}
                      number={num}
                      data={chartData[num]}
                      onUpdate={handleToothUpdate}
                      isUpper={false}
                    />
                  ))}
                </div>
                <div className="text-xs text-slate-500 text-center mt-1">Mandibular (Lower) - Buccal</div>
              </div>
            </div>
          </>
        )}

        {/* History Modal */}
        <Dialog open={showHistory} onOpenChange={setShowHistory}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Perio Chart History</DialogTitle>
            </DialogHeader>
            {chartHistory.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No previous charts recorded</p>
            ) : (
              <div className="space-y-4">
                {/* Group by date */}
                {[...new Set(chartHistory.map(e => e.chart_date))].sort().reverse().map(date => {
                  const dateEntries = chartHistory.filter(e => e.chart_date === date);
                  const avgDepth = (dateEntries.flatMap(e => e.probing_depths).reduce((a,b) => a+b, 0) / 
                                   dateEntries.flatMap(e => e.probing_depths).length).toFixed(1);
                  return (
                    <div key={date} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h3 className="font-semibold">{new Date(date).toLocaleDateString()}</h3>
                        <span className="text-sm text-slate-500">
                          {dateEntries.length} teeth | Avg: {avgDepth}mm
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {dateEntries.map(entry => (
                          <div 
                            key={entry.id} 
                            className={`px-2 py-1 rounded text-xs ${
                              Math.max(...entry.probing_depths) >= 6 ? 'bg-red-100 text-red-700' :
                              Math.max(...entry.probing_depths) >= 4 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}
                          >
                            #{entry.tooth_number}: {entry.probing_depths.join('-')}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PerioCharting;
